import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { createComponentLogger } from '../utils/logger';

const ErrorContainer = styled.div`
    padding: 2em;
    margin: 1em;
    border: 2px solid #dc3545;
    border-radius: 8px;
    background-color: #f8d7da;
    color: #721c24;
`;

const ErrorTitle = styled.h2`
    margin: 0 0 1em 0;
    color: #721c24;
`;

const ErrorMessage = styled.div`
    margin-bottom: 1em;
    font-size: 1.1em;
`;

const ErrorDetails = styled.details`
    margin-top: 1em;
    font-size: 0.9em;
`;

const ErrorSummary = styled.summary`
    cursor: pointer;
    margin-bottom: 0.5em;
    font-weight: 600;
`;

const ErrorStack = styled.pre`
    background-color: #f5f5f5;
    padding: 1em;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 0.85em;
    white-space: pre-wrap;
    word-wrap: break-word;
`;

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
    private logger = createComponentLogger('ErrorBoundary', 'render');

    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return {
            hasError: true,
            error,
        };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        this.logger.error('Error caught by boundary', {
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
        });

        this.setState({
            error,
            errorInfo,
        });
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <ErrorContainer data-testid="error">
                    <ErrorTitle>Something went wrong</ErrorTitle>
                    <ErrorMessage>
                        {this.state.error?.message || 'An unexpected error occurred'}
                    </ErrorMessage>
                    {this.state.errorInfo && (
                        <ErrorDetails>
                            <ErrorSummary>Error Details</ErrorSummary>
                            <ErrorStack>
                                {this.state.error?.stack}
                                {'\n\nComponent Stack:\n'}
                                {this.state.errorInfo.componentStack}
                            </ErrorStack>
                        </ErrorDetails>
                    )}
                </ErrorContainer>
            );
        }

        return this.props.children;
    }
}
